/**
 * ============================================================================
 *  File        : frontend/src/lib/csv.ts
 *  Module      : lib
 *  Purpose     : client-side CSV 匯出 helper（RFC 4180 quoting + Blob 下載）
 *  Author      : EF Engineering
 *  Created     : 2026-06-02
 *  Updated     : 2026-06-02
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  報表中心各 tab 共用：以「欄位定義（標頭 + 取值 fn）」把 row（含 p1/p2/p3 等
 *  巢狀物件）攤平成 CSV。值一律字串化;null/undefined → 空字串。含逗號 / 雙引號 /
 *  換行的值以雙引號包裹並 escape;UTF-8 BOM 前綴讓 Excel 正確辨識日中字元。
 */

/** 單一 CSV 欄位定義：標頭文字 + 從 row 取值。 */
export interface CsvColumn<TRow> {
  /** CSV 標頭（已 i18n 過的 user-facing 文字）。 */
  header: string
  /** 從 row 取出該欄值;回 null/undefined → 空字串。 */
  value: (row: TRow) => string | number | null | undefined
}

/** 將單一 cell 值轉為 RFC 4180 安全的字串。 */
function escapeCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return ''
  const str = String(value)
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

/** 依欄位定義把 rows 序列化為 CSV 文字（含標頭列）。 */
export function rowsToCsv<TRow>(rows: readonly TRow[], columns: readonly CsvColumn<TRow>[]): string {
  const headerLine = columns.map((c) => escapeCell(c.header)).join(',')
  const bodyLines = rows.map((row) => columns.map((c) => escapeCell(c.value(row))).join(','))
  return [headerLine, ...bodyLines].join('\r\n')
}

/**
 * 觸發瀏覽器下載 CSV（UTF-8 BOM + text/csv Blob）。
 * 以暫時 <a download> 點擊後 revoke object URL。
 */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/** 便利包裝：序列化 rows 並立即下載。 */
export function exportRowsToCsv<TRow>(
  filename: string,
  rows: readonly TRow[],
  columns: readonly CsvColumn<TRow>[],
): void {
  downloadCsv(filename, rowsToCsv(rows, columns))
}
